/**
 * PATH: apps/sam/src/features/email/components/MailboxChip.tsx
 * FILE_ID: SAM-EMAIL-CMP-006
 * PURPOSE: Per-row mailbox attribution chip — surfaces "this email
 *          landed in jake@'s mailbox" so the user can tell at a
 *          glance whose copy of a shared email they're looking at.
 * WHY:     The user explicitly asked: "There are obviously emails
 *          which are not from abhishek@allstylesteel.com.au but
 *          from Jake's email that have been allocated to job now
 *          shared / billing. when that is the case clearly show a
 *          colored card with tag from. whose email it is."
 * FLOW:
 *   ┌──────────────┐   ┌────────────────┐   ┌──────────────────┐
 *   │ syncedByEmail│ → │ mailboxColor() │ → │ pill / dot render │
 *   └──────────────┘   └────────────────┘   └──────────────────┘
 *
 * RELATED:
 *   - colorPalettes.ts: deterministic colour hash
 *   - EmailRow (in @quotech/ui): consumer slot
 *   - InboxPage: builder of the chip (knows current user)
 */

import { Inbox } from 'lucide-react'

import { cn, formatChipIdentity } from '@quotech/ui'
import { mailboxColor } from '../lib/colorPalettes'

interface MailboxChipProps {
  /** Email address of the mailbox that received this row. NULL on
   *  legacy pre-stamp rows; renders as a neutral "Unknown" chip in
   *  that case so the user knows the attribution is missing rather
   *  than thinking the row is theirs. */
  syncedByEmail?: string | null
  /** Friendly name of the owner (first + last). Used as the chip's
   *  visible label when present, falling back to the email's
   *  local-part. */
  syncedByName?: string | null
  /** True when the row's owner == the current viewer. Renders as a
   *  muted "you" chip so the user's own mail doesn't visually
   *  dominate over their teammates'. */
  isCurrentUser?: boolean
  /** Compact variant skips the icon to save horizontal space — used
   *  when the chip sits inside the row header next to the timestamp.
   *  Default is the regular variant with the leading icon. */
  compact?: boolean
  /** De-saturate the teammate chip to a neutral grey. Used on
   *  Kanban-FRAMED cards (operator 2026-06-16: the coloured mailbox
   *  pill "overlaps" / competes with the board frame + AI tag). The
   *  attribution is still readable, it just stops fighting the
   *  workflow colours for attention. No-op on the "you"/legacy chips,
   *  which are already muted. */
  neutral?: boolean
}

export function MailboxChip({
  syncedByEmail,
  syncedByName,
  isCurrentUser = false,
  compact = false,
  neutral = false,
}: MailboxChipProps) {
  if (!syncedByEmail && !isCurrentUser) {
    // Legacy / unstamped row — keep the layout balanced with a
    // muted question-mark chip rather than collapsing the slot,
    // so the column heights stay consistent across rows.
    return (
      <span
        title="Mailbox attribution unknown (legacy row)"
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          // Keep the unknown glyph readable — muted-foreground/70 on
          // bg-muted reads as transparent on the inbox command chrome.
          'border border-border bg-muted text-foreground',
          compact ? 'px-1.5 py-px text-[10px]' : 'px-2 py-0.5 text-[10px] gap-1',
        )}
      >
        {!compact && <Inbox className="h-2.5 w-2.5 shrink-0" />}
        ?
      </span>
    )
  }

  // The viewer's OWN mailbox is intentionally low-saturation so it
  // recedes — colour budget is better spent flagging teammates. But it
  // still names WHICH of the viewer's mailboxes a row landed in: when an
  // org has several connected mailboxes the host renders this even for
  // "you" (operator: "there are multiple mailboxes here … how do I know
  // which mailbox it is from"), so a bare "you" wouldn't disambiguate.
  // Show the local-part / first name, falling back to "you".
  if (isCurrentUser) {
    // Shared shortener (first name → local-part), but the viewer's OWN pill
    // says "you" rather than "Unknown" when there's nothing to name.
    const { label } = formatChipIdentity({ name: syncedByName, email: syncedByEmail })
    const ownLabel = label === 'Unknown' ? 'you' : label
    return (
      <span
        title={syncedByEmail ? `Your mailbox (${syncedByEmail})` : 'Your mailbox'}
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          'bg-muted/60 text-muted-foreground border border-border/50',
          compact ? 'px-1.5 py-px text-[10px] gap-0.5' : 'px-2 py-0.5 text-[10px] gap-1',
        )}
      >
        {!compact && <Inbox className="h-2.5 w-2.5 shrink-0" />}
        <span className="max-w-[90px] truncate">{ownLabel}</span>
      </span>
    )
  }

  const style = mailboxColor(syncedByEmail || '')
  // Owner name preference: first part of the friendly name (so the chip reads
  // as "Jake" not "Jake Smith"), falling back to the local-part of the email
  // (so it reads "abhishek", never "abhishek@finsoeasy.com"). Full address
  // stays in the `title` tooltip below.
  const { label } = formatChipIdentity({ name: syncedByName, email: syncedByEmail })

  return (
    <span
      title={syncedByEmail || 'Shared mailbox'}
      className={cn(
        'inline-flex items-center rounded-full font-medium border',
        neutral
          ? 'bg-muted/60 text-muted-foreground border-border/50'
          : cn(style.bg, style.text, style.border),
        compact ? 'px-1.5 py-px text-[10px] gap-0.5' : 'px-2 py-0.5 text-[10px] gap-1',
      )}
    >
      {!compact && <Inbox className="h-2.5 w-2.5 shrink-0" />}
      <span className="max-w-[80px] truncate">{label}</span>
    </span>
  )
}

/**
 * Multi-mailbox chip for deduped rows where the same conversation
 * landed in two or more mailboxes. Renders a stack of dots + a
 * tooltip listing the full set, so the user can see at a glance
 * "this email arrived in 2 of our shared mailboxes". Saves
 * horizontal space vs. rendering N independent chips.
 */
export function MailboxChipGroup({
  mailboxes,
  currentUserEmail,
  neutral = false,
}: {
  mailboxes: Array<{ email: string; name?: string | null }>
  currentUserEmail?: string | null
  /** De-saturate the dots/chip on Kanban-framed cards (see MailboxChip
   *  `neutral`). */
  neutral?: boolean
}) {
  if (!mailboxes.length) return null

  // Single mailbox: defer to the regular chip (cheaper, more
  // readable). The grouping is only useful when there's more than
  // one to aggregate.
  if (mailboxes.length === 1) {
    const mb = mailboxes[0]
    return (
      <MailboxChip
        compact
        neutral={neutral}
        syncedByEmail={mb.email}
        syncedByName={mb.name}
        isCurrentUser={
          !!currentUserEmail &&
          mb.email.toLowerCase() === currentUserEmail.toLowerCase()
        }
      />
    )
  }

  return (
    <span
      title={`Received in ${mailboxes.length} mailboxes:\n${mailboxes
        .map((m) => `· ${m.email}`)
        .join('\n')}`}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-[10px] font-medium',
        'bg-muted/60 text-muted-foreground border border-border/50',
      )}
    >
      <Inbox className="h-2.5 w-2.5 shrink-0" />
      <span className="flex items-center -space-x-0.5">
        {mailboxes.slice(0, 4).map((m) => {
          const isMe =
            !!currentUserEmail &&
            m.email.toLowerCase() === currentUserEmail.toLowerCase()
          const c =
            isMe || neutral
              ? { dot: 'bg-muted-foreground/50' }
              : mailboxColor(m.email)
          return (
            <span
              key={m.email}
              className={cn(
                'h-2 w-2 rounded-full ring-1 ring-background',
                c.dot,
              )}
            />
          )
        })}
      </span>
      <span>{mailboxes.length}</span>
    </span>
  )
}
